import React, { useState } from 'react';
import { FieldType } from '@frontend/app/types/globalTypes';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { MultiSelect } from '@frontend/app/components/MultiSelect/MultiSelect';
import { Button } from '@/shadcn/components/ui/button';
import { Input } from '@/shadcn/components/ui/input';
import { Label } from '@/shadcn/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shadcn/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { Textarea } from '@/shadcn/components/ui/textarea';
import { Calendar } from '@/shadcn/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/components/ui/popover';
import { cn } from '@/shadcn/lib/utils';
import { CustomFieldInputProps } from './types';

export const CustomFieldInput: React.FC<CustomFieldInputProps> = ({
  field,
  value,
  onChange,
  error,
  placeholder,
}) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Handle value changes with validation
  const handleValueChange = (newValue: number | string | boolean | string[] | undefined) => {
    onChange(newValue);
  };

  // Render number input
  const renderNumberInput = () => (
    <Input
      type="number"
      value={value ?? ''}
      onChange={(e) => {
        const numValue = e.target.value;
        // Convert to number if it's a valid number, otherwise keep as string for validation
        const convertedValue = numValue === '' ? '' : Number(numValue);
        handleValueChange(convertedValue);
      }}
      placeholder={placeholder || 'Enter number'}
      className={cn(error && 'border-red-500 w-full')}
    />
  );

  // Render text input
  const renderTextInput = () => {
    const isLongText = field.displayName.toLowerCase().includes('describe') ||
                      field.displayName.toLowerCase().includes('details');

    if (isLongText) {
      return (
        <Textarea
          value={value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          placeholder={placeholder || 'Enter text'}
          className={cn(error && 'border-red-500 w-full')}
          rows={3}
        />
      );
    }

    return (
      <Input
        type="text"
        value={value ?? ''}
        onChange={(e) => handleValueChange(e.target.value)}
        placeholder={placeholder || 'Enter text'}
        className={cn(error && 'border-red-500 w-full')}
      />
    );
  };

  // Render boolean select
  const renderBooleanInput = () => (
    <Select
      value={value === true ? 'yes' : value === false ? 'no' : ''}
      onValueChange={(val) => handleValueChange(val === 'yes')}
    >
      <SelectTrigger className={cn(error && 'border-red-500')}>
        <SelectValue placeholder="Select option" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="yes">Yes</SelectItem>
        <SelectItem value="no">No</SelectItem>
      </SelectContent>
    </Select>
  );

  // Render single select with radio buttons
  const renderSingleSelectInput = () => (
    <RadioGroup
      value={value != null ? String(value) : ''}
      onValueChange={handleValueChange}
      className="space-y-2"
    >
      {field.choices?.map((choice) => (
        <div key={choice.value} className="flex items-center space-x-2">
          <RadioGroupItem
            value={String(choice.value)}
            id={`radio-${choice.value}`}
            className={cn(error && 'border-red-500')}
          />
          <Label
            htmlFor={`radio-${choice.value}`}
            className="text-sm font-normal cursor-pointer"
          >
            {choice.displayName}
          </Label>
        </div>
      ))}
    </RadioGroup>
  );

  // Render multi-select using existing MultiSelect component
  const renderMultiSelectInput = () => {
    const selectedValues = Array.isArray(value) ? value : [];

    const options = field.choices?.map((choice) => ({
      label: choice.displayName,
      value: choice.value,
      searchValue: choice.displayName
    })) || [];

    return (
      <MultiSelect
        value={selectedValues}
        onChange={handleValueChange}
        options={options}
        placeholder="Select options"
        className={cn(error && 'border-red-500 w-full')}
      />
    );
  };

  // Render date picker
  const renderDateInput = () => (
    <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal',
            !value && 'text-grey-4',
            error && 'border-red-500'
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(new Date(value), 'MMM do, yyyy') : 'Select date'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value ? new Date(value) : undefined}
          onSelect={(date) => {
            // Convert date to ISO string for consistent handling
            const dateValue = date ? date.toISOString() : null;
            handleValueChange(dateValue);
            setIsDatePickerOpen(false);
          }}
          initialFocus
          className="w-full pointer-events-auto user-select-text"
        />
      </PopoverContent>
    </Popover>
  );

  // Main render function
  const renderInput = () => {
    switch (field.attributeType) {
      case FieldType.NUMBER:
        return renderNumberInput();
      case FieldType.TEXT:
        // For TEXT fields with choices, use radio buttons (single select)
        if (field.choices && field.choices.length > 0) {
          return renderSingleSelectInput();
        }
        return renderTextInput();
      case FieldType.BOOLEAN:
        return renderBooleanInput();
      case FieldType.DATE:
        return renderDateInput();
      case FieldType.ARRAY:
        return renderMultiSelectInput();
      default:
        // Default to single select for fields with choices
        if (field.choices && field.choices.length > 0) {
          return renderSingleSelectInput();
        }
        return renderTextInput();
    }
  };

  return (
    <div className="space-y-2 w-full">
      {renderInput()}
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};
